import { HolderOutlined } from '@ant-design/icons';
import type { ReferenceItem } from '@cult/schemas';
import { Button, Flex, Popconfirm, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import clsx from 'clsx';
import { type CSSProperties, type DragEvent, useCallback, useRef, useState } from 'react';
import { DataTable } from '../../../../../components';
import type { ReferenceHooks } from '../../types';
import styles from './ReferenceTable.module.scss';

const { Text } = Typography;

type Props = {
  items: ReferenceItem[];
  loading: boolean;
  onEdit: (item: ReferenceItem) => void;
  // Update hook from the resource — used for reorder, archive, and restore.
  useUpdate: ReferenceHooks['useUpdate'];
  // Lowercase singular noun for confirmation titles, e.g. "category".
  noun: string;
  archiveHint: string;
  restoreHint: string;
  // When false, the table is read-only: no drag-reorder, archive/restore, or edit controls.
  canEdit: boolean;
};

export const ReferenceTable = ({
  items,
  loading,
  onEdit,
  useUpdate,
  noun,
  archiveHint,
  restoreHint,
  canEdit,
}: Props) => {
  // Separate mutation instances so reorder's isPending doesn't bleed into Archive/Restore buttons.
  const reorder = useUpdate();
  const archiveMutation = useUpdate();
  const restoreMutation = useUpdate();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [rowHeight, setRowHeight] = useState(52);

  // Active items first (sortable), archived after (fixed at bottom).
  const active = items.filter((c) => !c.isArchived);
  const archived = items.filter((c) => c.isArchived);
  const rows = [...active, ...archived];

  // Per-row pending sets — useMutation tracks only the last call's variables, so rapid
  // clicks on different rows would kill the spinner on the first row. A Set avoids that.
  const [pendingArchive, setPendingArchive] = useState<Set<string>>(new Set());
  const [pendingRestore, setPendingRestore] = useState<Set<string>>(new Set());

  const addPending = useCallback((set: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => {
    set((s) => new Set(s).add(id));
  }, []);
  const removePending = useCallback((set: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => {
    set((s) => {
      const next = new Set(s);
      next.delete(id);
      return next;
    });
  }, []);

  const archive = (c: ReferenceItem) => {
    addPending(setPendingArchive, c.id);
    archiveMutation.mutate(
      { id: c.id, patch: { isArchived: true } },
      { onSettled: () => removePending(setPendingArchive, c.id) },
    );
  };
  const restore = (c: ReferenceItem) => {
    addPending(setPendingRestore, c.id);
    restoreMutation.mutate(
      { id: c.id, patch: { isArchived: false } },
      { onSettled: () => removePending(setPendingRestore, c.id) },
    );
  };

  // ── drag-and-drop (active rows only) ──────────────────────────────────────

  const handleDragStart = (index: number, e: DragEvent<HTMLElement>) => {
    setDragIndex(index);
    setRowHeight(e.currentTarget.getBoundingClientRect().height || 52);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const rowShift = (index: number): number => {
    if (dragIndex === null || overIndex === null || dragIndex === overIndex) return 0;
    if (index === dragIndex) return 0;
    if (overIndex > dragIndex && index > dragIndex && index <= overIndex) return -rowHeight;
    if (overIndex < dragIndex && index >= overIndex && index < dragIndex) return rowHeight;
    return 0;
  };

  const handleDragOver = (index: number, e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIndex !== null && dragIndex !== index && overIndex !== index) setOverIndex(index);
  };

  const handleWrapperDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (dragIndex === null) return;
    e.preventDefault();
    const tbody = wrapperRef.current?.querySelector('tbody');
    const firstRow = tbody?.querySelector<HTMLElement>('tr[draggable]');
    if (!tbody || !firstRow) return;
    const h = firstRow.offsetHeight || rowHeight;
    const relativeY = e.clientY - tbody.getBoundingClientRect().top;
    const last = active.length - 1;
    const next = Math.max(0, Math.min(last, Math.floor(relativeY / h)));
    if (next !== dragIndex && next !== overIndex) setOverIndex(next);
  };

  const handleWrapperDrop = (e: DragEvent<HTMLDivElement>) => {
    if (dragIndex === null || overIndex === null) return;
    e.preventDefault();
    handleDrop(overIndex);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      handleDragEnd();
      return;
    }
    const reordered = [...active];
    const [moved] = reordered.splice(dragIndex, 1);
    if (moved) reordered.splice(targetIndex, 0, moved);
    reordered.forEach((c, i) => {
      const next = (i + 1) * 10;
      if (c.sortOrder !== next) reorder.mutate({ id: c.id, patch: { sortOrder: next } });
    });
    handleDragEnd();
  };

  // ── columns ───────────────────────────────────────────────────────────────

  const dragColumn: ColumnsType<ReferenceItem>[number] = {
    title: '',
    key: 'drag',
    width: 36,
    render: (_v, c) =>
      c.isArchived ? null : (
        <HolderOutlined
          style={{ cursor: 'grab', color: 'var(--ant-color-text-tertiary, #8c8c8c)' }}
          aria-label="Drag to reorder"
        />
      ),
  };

  const actionsColumn: ColumnsType<ReferenceItem>[number] = {
    title: '',
    key: 'actions',
    width: 180,
    align: 'right',
    render: (_v, c) =>
      c.isArchived ? (
        <Popconfirm
          title={`Restore ${c.name}?`}
          description={restoreHint}
          okText="Restore"
          onConfirm={() => restore(c)}
        >
          <Button size="small" loading={pendingRestore.has(c.id)}>
            Restore
          </Button>
        </Popconfirm>
      ) : (
        <Space>
          <Popconfirm
            title={`Archive ${c.name}?`}
            description={archiveHint}
            okText="Archive"
            okButtonProps={{ danger: true }}
            onConfirm={() => archive(c)}
          >
            <Button size="small" danger loading={pendingArchive.has(c.id)}>
              Archive
            </Button>
          </Popconfirm>
          <Button size="small" onClick={() => onEdit(c)}>
            Edit
          </Button>
        </Space>
      ),
  };

  const columns: ColumnsType<ReferenceItem> = [
    ...(canEdit ? [dragColumn] : []),
    {
      title: '',
      key: 'color',
      width: 32,
      render: (_v, c) => (
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: 3,
            background: c.color ?? '#d9d9d9',
            border: '1px solid var(--ant-color-border, #f0f0f0)',
            opacity: c.isArchived ? 0.4 : 1,
          }}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v, c) => (
        <Flex align="center" gap={8}>
          <Text strong={!c.isArchived} type={c.isArchived ? 'secondary' : undefined}>
            {v}
          </Text>
          {c.isArchived ? <Tag style={{ marginInlineEnd: 0 }}>Archived</Tag> : null}
        </Flex>
      ),
    },
    ...(canEdit ? [actionsColumn] : []),
  ];

  const isDragging = dragIndex !== null;

  return (
    <DataTable<ReferenceItem>
      wrapperRef={wrapperRef}
      wrapperClassName={isDragging ? styles.tableDragging : undefined}
      wrapperProps={{
        onDragOver: handleWrapperDragOver,
        onDrop: handleWrapperDrop,
        'aria-label': `${noun} list`,
      }}
      columns={columns}
      dataSource={rows}
      loading={loading}
      onRow={(record, index) => {
        const i = index ?? 0;
        const isArchived = record.isArchived;
        const draggable = canEdit && !isArchived;
        const shift = draggable ? rowShift(i) : 0;
        return {
          draggable,
          onDragStart: draggable ? (e: DragEvent<HTMLElement>) => handleDragStart(i, e) : undefined,
          onDragOver: draggable ? (e: DragEvent<HTMLElement>) => handleDragOver(i, e) : undefined,
          onDrop: draggable ? () => handleDrop(i) : undefined,
          onDragEnd: draggable ? handleDragEnd : undefined,
          className: clsx(
            styles.row,
            !isArchived && dragIndex === i && styles.rowDragging,
            !isArchived && overIndex === i && dragIndex !== null && overIndex > dragIndex && styles.rowDropBelow,
            !isArchived && overIndex === i && dragIndex !== null && overIndex < dragIndex && styles.rowDropAbove,
          ),
          style: {
            '--row-shift': `${shift}px`,
            opacity: isArchived ? 0.5 : 1,
          } as CSSProperties,
        };
      }}
    />
  );
};
